import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Lock, ArrowUpRight, AlertCircle, Loader2 } from "lucide-react";
import { api, setToken, formatApiError } from "@/lib/api";
import { BRAND } from "@/lib/constants";
import usePageTitle from "@/lib/usePageTitle";

export default function AdminLoginPage() {
  usePageTitle("Admin Sign-in");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!username.trim() || !password) {
      setError("Please enter your username and password.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/admin/login", {
        username: username.trim(),
        password,
      });
      setToken(data.access_token);
      nav("/admin/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || "Unable to sign in.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      data-testid="admin-login-page"
      className="min-h-[calc(100vh-5rem)] flex items-center justify-center bg-[#F8FAFC] px-6"
    >
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest-plus text-[#B08A4A] mb-3">
            <Lock size={12} strokeWidth={1.5} />
            Restricted Access
          </div>
          <h1 className="font-serif text-4xl text-[#0A2540]">Admin Sign-in</h1>
          <p className="mt-2 font-sans text-sm text-[#475569]">
            {BRAND.name} — practice management console
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          data-testid="admin-login-form"
          className="bg-white border border-[#E2E8F0] p-8 lg:p-10 glow-navy"
        >
          <div className="mb-5">
            <label className="label-clean" htmlFor="username">
              Username
            </label>
            <input
              id="username"
              type="text"
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              data-testid="admin-input-username"
              className="input-clean"
              placeholder="Admin"
              autoFocus
            />
          </div>
          <div className="mb-6">
            <label className="label-clean" htmlFor="password">
              Password
            </label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-input-password"
              className="input-clean"
              placeholder="••••••••"
            />
          </div>

          {error && (
            <div
              data-testid="admin-login-error"
              className="mb-6 flex items-start gap-3 border border-red-200 bg-red-50 px-4 py-3 font-sans text-sm text-red-700"
            >
              <AlertCircle size={16} strokeWidth={1.5} className="mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="w-full btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <Loader2 size={14} strokeWidth={1.8} className="animate-spin" />
                Signing in…
              </>
            ) : (
              <>
                Sign in
                <ArrowUpRight size={14} strokeWidth={1.5} />
              </>
            )}
          </button>

          <p className="mt-6 text-center font-mono text-[10px] uppercase tracking-widest-plus text-[#94A3B8]">
            Sessions expire after 24 hours
          </p>
        </form>

        <div className="mt-6 text-center">
          <Link
            to="/"
            className="font-mono text-[10px] uppercase tracking-widest-plus text-[#475569] hover:text-[#0A2540] transition-colors"
          >
            ← Return to site
          </Link>
        </div>
      </div>
    </section>
  );
}
